import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, Medal, Sparkles, Trophy, User as UserIcon } from 'lucide-react';

import { useGame } from '../../context/GameContext';
import { useSurface } from '../../hooks/useSurface';
import { Avatar } from './Avatar';
import { Page } from './Page';

/**
 * ONE dashboard template. Five screens.
 *
 * Profile · Stats · Game history · Achievements · Leaderboard are five views
 * of the same thing — "how am I doing" — and on a phone they're five separate
 * destinations reached through a menu, which is fine, because a phone can only
 * hold one at a time.
 *
 * On a laptop that's absurd: you'd have to go back to a hamburger menu to get
 * from your stats to your history. So above 1024 they become a SplitLayout:
 * an identity + nav column (280px) that never moves, and the content beside
 * it. Same five screens, same five routes, same components — one shared frame.
 *
 * Below 1024 this component renders its children and NOTHING ELSE. The phone
 * keeps its menu, its back button, and its single column, untouched.
 */
const DASH_LINKS = [
  { icon: UserIcon, label: 'Profile', path: '/profile' },
  { icon: BarChart3, label: 'Stats', path: '/stats' },
  { icon: Sparkles, label: 'Game history', path: '/history' },
  { icon: Trophy, label: 'Achievements', path: '/achievements' },
  { icon: Medal, label: 'Leaderboard', path: '/leaderboard' },
];

export interface DashboardPageProps {
  children: React.ReactNode;
  /** Rendered above the split — the screen's TopBar. */
  top?: React.ReactNode;
}

/**
 * Wrap a dashboard screen's body. Use as:
 *
 *   <DashboardPage top={<TopBar … />}>
 *     …the screen…
 *   </DashboardPage>
 */
export function DashboardPage({ children, top }: DashboardPageProps) {
  const surface = useSurface();
  const desktop = surface === 'desktop';

  return (
    <Page variant={desktop ? 'wide' : 'content'}>
      {top}
      {desktop ? (
        <div className="wr-split">
          <aside className="split-side">
            <DashboardNav />
          </aside>
          <div style={{ minWidth: 0 }}>{children}</div>
        </div>
      ) : (
        children
      )}
    </Page>
  );
}

/** Identity + the five destinations. Desktop only — see DashboardPage. */
function DashboardNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useGame();
  const me = state.currentPlayer;

  return (
    <div className="wr-col gap-3">
      {me && (
        <div className="card lg wr-col gap-3" style={{ alignItems: 'center', textAlign: 'center' }}>
          <Avatar id={me.avatar} size={72} />
          <div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 18, letterSpacing: '-0.01em' }}>
              {me.name}
            </div>
            <button
              type="button"
              onClick={() => navigate('/settings')}
              style={{
                marginTop: 2,
                minHeight: 44,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--ink-3)',
                fontFamily: 'var(--ui)',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Settings →
            </button>
          </div>
        </div>
      )}

      <nav className="wr-col gap-1" aria-label="Dashboard">
        {DASH_LINKS.map(link => {
          const Icon = link.icon;
          const active = location.pathname.startsWith(link.path);
          return (
            <button
              key={link.path}
              type="button"
              onClick={() => navigate(link.path)}
              className="wr-row gap-3"
              style={{
                minHeight: 48,
                padding: '0 14px',
                alignItems: 'center',
                borderRadius: 14,
                border: 'none',
                cursor: 'pointer',
                background: active ? 'var(--accent-soft)' : 'transparent',
                color: active ? 'var(--accent)' : 'var(--ink-2)',
                fontFamily: 'var(--ui)',
                fontWeight: 600,
                fontSize: 15,
                textAlign: 'left',
              }}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={18} strokeWidth={2.2} />
              <span>{link.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
